<template>
  <div>
    <full-screen-loader v-if="rootChatLoading"/>
    <smd-one-col-layout class="inbox-message"
                        content-styles="hide-overflow full-width">
      <smd-header slot="header"
                  :nav="$isDesktop">
        <smd-btn class="back-btn"
                 icon="ns-icon-ico028"
                 slot="left-actions"
                 @click="backToMessages"/>
        <h2 class="Title3 gray-400"
            v-if="pair"
            :class="{'m-l-20': !$isDesktop}">
          {{ pair.doctor._fullName }}
        </h2>
        <span class="ns-icon-ico001"
              slot="right-actions"
              v-if="$isMobile">
        <span class="path1"></span>
        <span class="path2"></span>
      </span>
      </smd-header>

      <smd-one-col-layout class="chat-container"
                          scrollable-content-id="scroll-el">
        <p v-if="error"
           class="above error">
          {{ error }}
        </p>

        <div v-if="loading"
             class="above">
          <smd-loading-spinner/>
        </div>

        <div class="messages">
          <div class="empty-state"
               v-if="!reverseMessages.length">
            <p class="gray-400">No messages shared yet.</p>
          </div>
          <div class="message-container"
               v-for="message in reverseMessages"
               :class="computedClass(message)"
               :key="message.id">

            <message-profile-pic v-if="!isPatientMessage(message)"
                                 :profile-pic="pair.doctor.profile_url"
                                 :name="pair.doctor.name"/>

            <component :from-patient="isPatientMessage(message)"
                       :is="activeComponent(message)"
                       :message="message"
                       :token="token"
                       :patient="pair.patient"
                       @loaded="imageLoaded"
                       @root-chat-loading="setRootChatLoading"/>
          </div>
        </div>
      </smd-one-col-layout>

      <div class="inputs-container smd-form__row"
           slot="footer">
        <smd-btn class="attachment-btn"
                 icon="ns-icon-attachment"
                 @click="selectFiles"/>
        <input hidden
               type="file"
               :accept="$options.attachmentAccept"
               :ref="$options.attachmentRef"
               @change="onFileInput">
        <input maxlength="255"
               placeholder="Type message here..."
               type="text"
               v-model="message"
               @keyup.enter="send">
        <smd-btn class="send-btn"
                 icon="ns-icon-send"
                 @click="send"/>
      </div>
    </smd-one-col-layout>
  </div>
</template>

<script>
  import Vue from 'vue';
  import moment from 'moment';

  import MessageProfilePic from '@/components/messages/MessageProfilePic';
  import MessageSession from '@/components/messages/MessageSession';
  import MessageAttachment from '@/components/messages/MessageAttachment';
  import MessageMultipleAttachments from "@/components/messages/message-multiple-attachments";

  import MessageFiles from '@/components/messages/MessageFiles';
  import MessageBill from '@/components/messages/MessageBill';
  import MessagePlainText from '@/components/messages/MessagePlainText';
  import MessageEduc from "@/components/messages/MessageEduc";
  import MessageLabRequest from "@/components/messages/message-lab-request";
  import MessagePrescription from "@/components/messages/message-prescription";
  import MessageFreestyleSignupPrescription from '@/components/messages/message-freestyle-signup-prescription';
  import socketMixin from '@/mixins/socket';

  import {mapGetters, mapActions} from 'vuex';
  import {v4 as uuidv4} from 'uuid';
  import {MessageService} from '@/services/messages';
  import {
    getMessageMomentDate,
    addMessageFromProp,
    setDoctorSocketMessageType,
    transformReceivedMessage,
  } from '@/utils/messages';
  import {
    getPrescriptionCoupon,
  } from '@/utils/medicine';
  import {getFileExtension} from '@/utils/files';
  import {logger, redact} from '@/utils/logger';
  import {MSG_TYPES, ATTACHMENT_ACCEPT} from '@/constants/inbox-messages';
  import {PageTypes} from "@/reference";
  import {
    isFSLEProgramId,
  } from '@/reference/coupon';
  import {SOCKET_EVENT} from '@/constants/socket';
  import GetElementStringMixin from '@/mixins/get-element-string';
  import {
    GETTER_TOKENS,
    GETTER_DOCTOR_PATIENT_PARTNER,
    ACTION_ADD_DOCTOR_LATEST_MESSAGES,
    ACTION_REMOVE_UNREAD_MESSAGE_COUNT,
  } from '@/store/types';
  import {
    MESSAGES_ROUTE,
    MESSAGE_DOCTOR_PARAM,
    MESSAGE_PATIENT_ID_PARAM,
    ERROR_ROUTE,
  } from '@/router/routes';

  export default {
    name: 'root-chat',
    mixins: [
      socketMixin,
      GetElementStringMixin,
    ],
    components: {
      MessagePlainText,
      MessageFiles,
      MessageSession,
      MessageProfilePic,
      MessageAttachment,
      MessageBill,
      MessageEduc,
    },
    data() {
      return {
        pair: null,
        token: null,
        message: '',
        messages: [],
        loading: false,
        rootChatLoading: false,
        error: null,
        errorTimeoutRef: null,
        scrollEl: null,

        /**
         * primary used to scroll down when page is loaded
         * one case is when scrolled after pushing initial message,
         * images are still loading that causes to scroll up when loaded
         *
         * so the solution is if the image is loaded on component,
         * scroll down again
         *
         * change to false if:
         * - user tries to fetch past message
         * - received new message
         * - send new message
         */
        onInitialState: true,

        socketListeners: [
          {
            event: SOCKET_EVENT.ROOM_MESSAGE,
            callback: this.appendNewMessage
          },
          {
            event: SOCKET_EVENT.SHARED_FILE,
            callback: this.appendSharedFile,
          }
        ],
      };
    },
    computed: {
      ...mapGetters({
        tokens: GETTER_TOKENS,
        doctorPatientPartner: GETTER_DOCTOR_PATIENT_PARTNER,
      }),
      reverseMessages() {
        return this.messages
          .slice()
          .filter(({msgtype}) => Object.values(MSG_TYPES).includes(msgtype))
          .reverse();
      },
      lastMessageTimestamp() {
        if (!this.messages.length) {
          return (new Date()).getTime();
        }

        return moment(this.messages[this.messages.length - 1].date).valueOf();
      },
    },
    methods: {
      ...mapActions({
        setDoctorLastMessage: ACTION_ADD_DOCTOR_LATEST_MESSAGES,
        resetUnreadCount: ACTION_REMOVE_UNREAD_MESSAGE_COUNT,
      }),
      activeComponent(message) {
        switch (message.msgtype) {
          case (this.$options.messageTypes.attachment):
            return MessageAttachment;
          case (this.$options.messageTypes.multipleAttachments):
            return MessageMultipleAttachments;
          case (this.$options.messageTypes.message):
          case (this.$options.messageTypes.text):
            return MessagePlainText;
          case (this.$options.messageTypes.session):
            return MessageSession;
          case (this.$options.messageTypes.file):
            return message.pagetype === PageTypes.educ ? MessageEduc : MessageFiles;
          case (this.$options.messageTypes.labtest):
            return MessageLabRequest;
          case (this.$options.messageTypes.prescription): {
            const coupon = getPrescriptionCoupon(message);

            if (coupon) {
              if (isFSLEProgramId(coupon.programid)) {
                return MessageFreestyleSignupPrescription;
              }
            }

            return MessagePrescription;
          }
          case (this.$options.messageTypes.pxbill):
            return MessageBill;
        }
      },
      backToMessages() {
        this.$router.push({name: MESSAGES_ROUTE});
      },
      computedClass(message) {
        return {
          'right': this.isPatientMessage(message),
          'image-file': message.msgtype === this.$options.messageTypes.attachment,
          'bill-message': message.msgtype === this.$options.messageTypes.pxbill
        }
      },
      initialize() {
        this.loading = true;

        this.token = Object.keys(this.doctorPatientPartner)
          .find((pairToken) => {
            const {doctor, patient} = this.doctorPatientPartner[pairToken];
            const doctorMatched = this.$route.params[MESSAGE_DOCTOR_PARAM] === doctor.usertag;
            const patientMatched = this.$route.params[MESSAGE_PATIENT_ID_PARAM] === patient.id;
            return doctorMatched && patientMatched;
          });

        if (this.token) {
          this.pair = this.doctorPatientPartner[this.token];
          this.fetchMessages(true);
        } else {
          this.$router.push({name: ERROR_ROUTE});
        }
      },
      async fetchMessages(initialLoad = false) {
        this.loading = true;
        this.error = null;

        try {
          const lastMessageTimestamp = this.messages.length
            ? moment(getMessageMomentDate(this.messages[this.messages.length - 1])).valueOf()
            : (new Date()).getTime();
          const {data: messages} = await MessageService.getMessagesCombined(lastMessageTimestamp, this.token);

          if (initialLoad) {
            this.messages = [];

            messages.forEach((message) => {
              addMessageFromProp(message);
              this.messages.push(transformReceivedMessage(message));
              // expected only to actually scroll to bottom if there's no images on messages
              // else, it will still try but scrolls up when images are loaded
              // on that case, the imageLoaded will trigger the scroll to bottom again
              this.scrollToBottom(true);
            });

            if (!this.messages.length) {
              this.messages.push({
                from: 1,
                date: (new Date()).toISOString(),
                msg: this.getElementString('txt610', {strings: [this.pair.patient.firstname, this.pair.doctor.name]}),
                msgtype: this.$options.messageTypes.message,
              });
            }

            this.resetUnreadCount(this.pair.localStorageKey);
          } else {
            messages.forEach((message) => {
              if (getMessageMomentDate(message) < lastMessageTimestamp) {
                addMessageFromProp(message);
                this.messages.push(transformReceivedMessage(message));
              }
            });
          }

          this.loading = false;
        } catch (error) {
          this.loading = false;
          this.error = 'Failed to fetch your messages.';
        }
      },
      selectFiles() {
        this.$refs[this.$options.attachmentRef].click();
      },
      async onFileInput(e) {
        const file = e.target.files[0];

        if (!file) {
          return;
        }

        const maxFileSize = 10485760; // 10 MB

        if (file.size > maxFileSize) {
          this.$refs[this.$options.attachmentRef].value = null;
          await this.$alert.show({
            text: 'Your file has exceed the maximum size of 10 MB',
            buttons: [
              {value: null, label: 'Okay'},
            ],
          });
          return;
        }

        // a 0-byte file is truthy and passes the size cap, but uploads an empty
        // multipart part the API cannot tell apart from no file at all
        if (!file.size) {
          this.$refs[this.$options.attachmentRef].value = null;
          await this.$alert.show({
            text: 'That file is empty. Please pick another file.',
            buttons: [
              {value: null, label: 'Okay'},
            ],
          });
          return;
        }

        const message = this.message;
        this.message = '';

        try {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('message', message);

          const {data} = await MessageService.uploadAttachment(formData, this.token);

          if (!data) {
            throw new Error('EMPTY_RESPONSE_BODY');
          }

          this.$refs[this.$options.attachmentRef].value = null;
          this.appendNewAttachment(data.uploadid, message);
        } catch (error) {
          logger.error('Attachment upload failed', {
            fileSize: file.size,
            fileType: file.type || 'UNKNOWN',
            fileExtension: getFileExtension(file.name),
            fileName: redact(file.name),
            status: error.response && error.response.status,
            result: error.response && error.response.data && error.response.data.result,
            reason: error.message,
            isNetworkError: !!error.isAxiosError && !error.response,
            code: error.code,
          }, error);

          this.$refs[this.$options.attachmentRef].value = null;
          this.error = `Failed to upload ${file.name}.`;
        }
      },
      appendSharedFile(data) {
        if (data.token === this.pair.token) {
          if (data.mimetype) {
            setDoctorSocketMessageType(data);
            this.messages.unshift(data);
          }

          this.setDoctorLastMessage({
            [this.pair.localStorageKey]: data,
          });
        }

        this.scrollToBottom(true);
      },
      appendNewAttachment(uploadid, message) {
        this.messages.unshift({
          datecreated: moment().toISOString(),
          from: 0,
          message,
          msgtype: this.$options.messageTypes.attachment,
          uploadid,
        });

        this.scrollToBottom(true);
      },
      async send() {
        if (this.message === '') {
          return;
        }

        const message = this.message;
        this.message = '';

        try {
          const {data} = await MessageService.sendMessage(message, this.token, uuidv4());

          if (!data) {
            throw Error();
          }
        } catch (error) {
          this.error = 'There was an error sending your message. Please try again.';
        }
      },
      appendNewMessage(data) {
        const isDuplicate = !!this.reverseMessages.find(({id}) => id === data.id);

        if (data.token === this.pair.token && !isDuplicate) {
          setDoctorSocketMessageType(data);

          if (data.type === this.$options.messageTypes.pxbill) {
            this.messages.unshift(transformReceivedMessage({
              ...data,
              msg: JSON.parse(data.msg),
            }));
          } else {
            this.messages.unshift(transformReceivedMessage(data));
          }

          this.setDoctorLastMessage({
            [this.pair.localStorageKey]: data,
          });
        }

        this.scrollToBottom(true);
      },
      scrollToBottom(onNextTick = false) {
        if (onNextTick) {
          Vue.nextTick(() => {
            this.scrollEl.scrollTop = this.scrollEl.scrollHeight;
          });
        } else {
          this.scrollEl.scrollTo({
            top: this.scrollEl.scrollHeight,
            behavior: 'smooth',
          })
        }
      },
      handleScrollToTop() {
        const {scrollTop} = this.scrollEl;

        if (scrollTop === 0) {
          this.onInitialState = false;
          this.fetchMessages();
        }
      },
      isPatientMessage({from}) {
        return from === 0;
      },
      imageLoaded() {
        if (this.onInitialState) {
          this.scrollToBottom();
        }
      },
      setRootChatLoading(value) {
        console.log(value, 'loading root chat')
        this.rootChatLoading = value
      }
    },
    watch: {
      doctorPatientPartner: {
        handler(newValue) {
          if (newValue) {
            this.initialize();
          }
        },
        immediate: true,
      },
      error(error) {
        if (error) {
          if (this.errorTimeoutRef) {
            clearTimeout(this.errorTimeoutRef);
          }

          this.errorTimeoutRef = setTimeout(() => {
            this.error = null;
          }, 2000);
        }
      },
    },
    mounted() {
      this.scrollEl = document.getElementById("scroll-el");
      this.scrollEl.addEventListener('scroll', this.handleScrollToTop);
    },
    beforeDestroy() {
      this.scrollEl.removeEventListener('scroll', this.handleScrollToTop);
    },
    attachmentRef: 'attachmentRef',
    attachmentAccept: ATTACHMENT_ACCEPT,
    messageTypes: MSG_TYPES,
  }
</script>

<style lang="scss">
  .inbox-message {

    .header {
      display: flex;
      align-items: center;
      padding: space(s);

      .back-btn {
        margin-right: space(m);
        color: color(label, text);
      }

      .doctor-name {
        font-size: font(l);
        font-weight: font(medium);
        margin-left: space(m);
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
      }
    }

    .chat-container {
      background: color(white);

      .messages {
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        min-height: 100%;

        @include responsive(desktop) {
          padding: space(s);
        }
      }

      .above {
        position: sticky;
        top: 0;
        z-index: 1;
        margin-bottom: auto;
        padding: space(xs) space(m);
      }

      .error {
        text-align: center;
        color: color(white, text);
        padding: space(xs) space(m);
        background-color: color(red, background);
      }

      .message-container {
        display: flex;
        max-width: 84%;

        @include responsive(desktop) {
          max-width: 60%;
        }

        &.bill-message {
          max-width: 95%;
        }

        &.image-file {
          max-width: 45%;
        }

        &.right {
          display: block;
          margin-left: auto;
        }

        &:first-child {
          margin-top: space(s);
        }

        &:not(:last-child) {
          margin-bottom: space(s);
        }
      }
    }

    .inputs-container {
      display: flex;
      align-items: center;
      padding: space(s);
      background-color: color(base, background);
      z-index: 100;

      input {
        flex: 1;
        height: 32px;
        padding: space(s) space(m);
        margin: 0 space(m);
        border: none;
        outline: none;
        box-shadow: none;
        border-radius: 16px;
        background-color: color(dark, background);
      }

      .send-btn {
        color: color(blue, brand);
      }
    }

    .empty-state {
      text-align: center;
      margin: auto;
    }
  }
</style>
